import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import axiosInstance from '../../api/api';
import { toast } from '../../hooks/use-toast';
import {
  PaperPlaneTilt, X, Eye, CheckCircle, XCircle, Copy, ArrowSquareOut, EnvelopeSimple
} from '@phosphor-icons/react';

const STATUS_META = {
  sent: { label: 'Sent', cls: 'bg-neutral-200 text-neutral-700', Icon: PaperPlaneTilt },
  viewed: { label: 'Viewed', cls: 'bg-blue-100 text-blue-700', Icon: Eye },
  accepted: { label: 'Accepted', cls: 'bg-green-100 text-green-700', Icon: CheckCircle },
  declined: { label: 'Declined', cls: 'bg-red-100 text-red-700', Icon: XCircle },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;

export const ProposalsTrackerSidebar = ({ isOpen, onClose }) => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/proposals/mine');
      if (r.data?.success) setProposals(r.data.proposals || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  if (!isOpen) return null;

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/proposal/${token}`);
    toast({ title: 'Proposal link copied' });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="proposals-tracker-sidebar">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-[#0F172A] border-l-2 border-black dark:border-white h-full overflow-y-auto">
        <div className="sticky top-0 p-4 border-b-2 border-black dark:border-white bg-[#2B4C6F] text-white flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <PaperPlaneTilt size={20} weight="fill" />
            <h3 className="font-black uppercase tracking-wide">My Proposals</h3>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded" data-testid="close-proposals-tracker"><X size={20} /></button>
        </div>

        {loading && (
          <div className="p-4 space-y-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-28 bg-neutral-100 dark:bg-neutral-800 animate-pulse" />)}
          </div>
        )}

        {!loading && proposals.length === 0 && (
          <div className="p-8 text-center">
            <PaperPlaneTilt size={48} weight="light" className="text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">No proposals sent yet</p>
            <p className="text-xs text-neutral-400 mt-2">Generate an estimate, then hit "Send to Client" to create your first proposal.</p>
          </div>
        )}

        {!loading && proposals.length > 0 && (
          <div className="p-4 space-y-3">
            {proposals.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.sent;
              return (
                <div key={p.id} className="border-2 border-black dark:border-white p-4" data-testid={`proposal-row-${p.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <h4 className="font-bold text-neutral-900 dark:text-white truncate">
                        {p.client_name || p.client_email || 'Unnamed client'}
                      </h4>
                      <p className="text-xs text-neutral-500 uppercase">{(p.job_type || 'project').replace(/_/g, ' ')}{p.location ? ` · ${p.location}` : ''}</p>
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 ${meta.cls}`} data-testid={`proposal-status-${p.id}`}>
                      <meta.Icon size={12} weight="bold" /> {meta.label}
                    </span>
                  </div>

                  <div className="text-xs font-mono text-neutral-500 space-y-0.5 mb-3">
                    <p>Sent {fmtDate(p.created_at)}{p.viewed_at ? ` · Viewed ${fmtDate(p.viewed_at)}` : ''}{p.responded_at ? ` · ${p.status === 'accepted' ? 'Accepted' : 'Declined'} ${fmtDate(p.responded_at)}` : ''}</p>
                    {p.status === 'accepted' && p.accepted_tier && (
                      <p className="text-green-700 dark:text-green-400 font-bold">
                        {p.accepted_tier.charAt(0).toUpperCase() + p.accepted_tier.slice(1)} tier{p.accepted_total ? ` — $${Number(p.accepted_total).toLocaleString()}` : ''}
                      </p>
                    )}
                    {(p.status === 'sent' || p.status === 'viewed') && p.price_range && (
                      <p>Range: {p.price_range}</p>
                    )}
                    {p.client_email && <p className="flex items-center gap-1 truncate"><EnvelopeSimple size={12} /> {p.client_email}</p>}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => copyLink(p.token)}
                      variant="outline"
                      className="h-8 flex-1 text-xs font-bold uppercase border-2 border-black dark:border-white rounded-none"
                      data-testid={`copy-proposal-link-${p.id}`}
                    >
                      <Copy size={14} className="mr-1" /> Copy Link
                    </Button>
                    <Button
                      onClick={() => window.open(`/proposal/${p.token}`, '_blank')}
                      variant="outline"
                      className="h-8 flex-1 text-xs font-bold uppercase border-2 border-black dark:border-white rounded-none"
                      data-testid={`open-proposal-${p.id}`}
                    >
                      <ArrowSquareOut size={14} className="mr-1" /> Open
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
